import Paragraph from "@common/components/Paragraph/Paragraph";
import { vars } from "@common/styles";
import styled from "styled-components";

/**
 * Rows in BaseTable are `display: contents` wrappers, so their cells become direct children of
 * the one table grid. An interjected row is a single element instead, so it has to span every
 * column explicitly, and uses `subgrid` so its own children (info icon + content / action / dismiss)
 * sit on the table's real column tracks. The "Created" column is deliberately left as a
 * placeholder - no cell is placed there, so it just sits empty between action and dismiss,
 * matching the gap a real row has in that spot.
 */
export const Row = styled.div`
  grid-column: 1 / -1;

  display: grid;
  grid-template-columns: subgrid;
  align-items: center;

  box-sizing: border-box;
  min-height: 4.8rem;

  border-radius: 1rem;
  background-color: ${vars.colors.grey100};

  user-select: text;

  font-size: 1.4rem;
  line-height: 1.7;
`;

export const TitleCell = styled.div`
  grid-column: 1 / 3;
  display: flex;
  align-items: center;
  min-width: 0;
  padding-left: 1rem;
`;

export const InfoIconWrap = styled.div`
  display: flex;
  align-items: center;
  padding-right: 13.5rem;
  flex-shrink: 0;
`;

export const ActionCell = styled.div`
  grid-column: 3;
  display: flex;
  align-items: center;
  min-width: 0;
  padding-left: 1rem;
`;

export const DismissCell = styled.div`
  grid-column: -2 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  padding-left: 1rem;
`;

export const IconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  flex-shrink: 0;
`;

export const Icon = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const Content = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 1rem;
`;

export const Info = styled.img`
  cursor: help;
`;

export const Title = styled(Paragraph)`
  min-width: 0;
  max-width: 40rem;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  color: ${vars.colors.grey30};
`;

export const Description = styled(Paragraph)`
  min-width: 0;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  color: ${vars.colors.grey60};
`;

export const ActionButton = styled.button`
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0;
  border: 1px solid ${vars.colors.grey60};
  background: ${vars.colors.grey80};
  border-radius: 0.6rem;
  padding: 0.5rem 1rem;
  align-items: center;
  justify-content: center;
  text-wrap: nowrap;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

export const DismissButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  width: 2.4rem;
  height: 2.4rem;
  padding: 0;

  border: none;
  background: none;
  cursor: pointer;

  opacity: 0.6;
  transition: ${vars.transitions.hover};

  &:hover {
    opacity: 1;
  }
`;

export const FeedbackForm = styled.form`
  display: flex;
  align-items: center;
  flex: 1;
`;

export const FeedbackInput = styled.input`
  flex: 1;
  min-width: 0;

  box-sizing: border-box;
  height: 3.2rem;
  padding: 0 1.2rem;

  border: 1px solid ${vars.colors.grey90};
  border-radius: 1rem;
  background-color: ${vars.colors.black};

  color: ${vars.colors.white};
  font-family: ${vars.fonts.fontFamily};
  font-size: 1.4rem;

  &::placeholder {
    color: ${vars.colors.grey60};
  }

  &:focus {
    outline: none;
    border-color: ${vars.colors.purple};
  }
`;

export const ThanksNote = styled.span`
  flex: 1;
  color: ${vars.colors.grey30};
`;
